import React, { CSSProperties, ReactNode, useCallback, useEffect, useState } from 'react';
import DialogBase from '../../../base/DialogBase/DialogBase';
import { HealthCheck, useAssignClientsHealthChecksMutation, useGetHealthCheckListLazyQuery, useGetClientLazyQuery } from '../../../generated/graphql';
import { FormMultiSelect, FormCheckbox } from '../../form';
import styles from './AddClientHealthChecksDialog.module.css';

type AssignmentStatus = 'all' | 'some' | 'none';

type AddClientHealthChecksDialogProps = {
    onClose: () => void,
    ids: string[],
    visible: boolean,
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
};

const AddClientHealthChecksDialog = (props: AddClientHealthChecksDialogProps) => {
    const { ids, style, visible, onClose } = props;
    const [assignClientsHealthChecks, assignClientsHealthChecksResult] = useAssignClientsHealthChecksMutation();
    const [selectedHealthChecks, setSelectedHealthChecks] = useState<HealthCheck[]>([]);
    const [overwrite, setOverwrite] = useState(false);
    const [validationError, setValidationError] = useState('');
    const [allHealthChecks, setAllHealthChecks] = useState<HealthCheck[]>([]);
    const [assignedMap, setAssignedMap] = useState<Record<string, AssignmentStatus>>({});
    const [getHealthCheckList] = useGetHealthCheckListLazyQuery({ fetchPolicy: "no-cache" });
    const [getClient] = useGetClientLazyQuery({ fetchPolicy: "no-cache" });

    const fetchAllHealthChecks = useCallback(async () => {
        let after: string | undefined;
        let accumulated: HealthCheck[] = [];
        let hasNextPage = true;

        while (hasNextPage) {
            const { data } = await getHealthCheckList({
                variables: { contains: "", after },
            });
            const nodes = (data?.HealthCheckList?.nodes || []) as HealthCheck[];
            accumulated = [...accumulated, ...nodes];
            hasNextPage = data?.HealthCheckList?.pageInfo?.hasNextPage ?? false;
            after = data?.HealthCheckList?.pageInfo?.endCursor ?? undefined;
        }

        setAllHealthChecks(accumulated);
    }, [getHealthCheckList]);

    const fetchAssignedHealthChecks = useCallback(async () => {
        const clientResults = await Promise.all(
            ids.map(id => getClient({ variables: { id } }))
        );

        const healthCheckCounts: Record<string, number> = {};
        clientResults.forEach(result => {
            const clientHealthCheckIds = result.data?.Client?.healthCheckIds || [];
            clientHealthCheckIds.forEach(hcId => {
                if (hcId) {
                    healthCheckCounts[hcId] = (healthCheckCounts[hcId] || 0) + 1;
                }
            });
        });

        const totalClients = ids.length;
        const statusMap: Record<string, AssignmentStatus> = {};
        Object.entries(healthCheckCounts).forEach(([hcId, count]) => {
            statusMap[hcId] = count >= totalClients ? 'all' : 'some';
        });

        setAssignedMap(statusMap);
    }, [ids, getClient]);

    useEffect(() => {
        fetchAllHealthChecks();
        fetchAssignedHealthChecks();
    }, [fetchAllHealthChecks, fetchAssignedHealthChecks]);

    const renderItem = (item: HealthCheck) => {
        const status = assignedMap[item.id || ''];
        const badge = status === 'all'
            ? <span className={styles.assignedBadge}>Assigned</span>
            : status === 'some'
                ? <span className={styles.partialBadge}>Partially Assigned</span>
                : null;

        return (
            <span className={styles.itemContent}>
                <span>{item.name}</span>
                {badge}
            </span>
        );
    };

    const handleSubmit = () => {
        if (selectedHealthChecks.length === 0) {
            setValidationError('Please fill all of the following fields.');
            return;
        }
        setValidationError('');
        const healthCheckIds = selectedHealthChecks.map(t => t.id || "");
        assignClientsHealthChecks({
            variables: { ids, healthCheckIds, overwrite }
        })
            .then(() => { onClose(); })
            .catch(() => {});
    };

    return (
        <DialogBase
            title="Add Health Checks"
            type="OkCancel"
            visible={visible}
            onClose={onClose}
            onSubmit={handleSubmit}
            validationError={validationError}
            className={styles.AddClientHealthChecksDialog}
            style={style}
            data-testid="AddClientHealthChecksDialog"
            isLoading={assignClientsHealthChecksResult.loading}>
            <FormMultiSelect
                label="Health Checks"
                name="healthChecks"
                value={selectedHealthChecks}
                onChange={(v) => setSelectedHealthChecks(v as HealthCheck[])}
                options={allHealthChecks}
                textField="name"
                renderItem={renderItem}
            />
            <FormCheckbox
                label="Overwrite"
                name="overwrite"
                checked={overwrite}
                onChange={setOverwrite}
            />
        </DialogBase>
    );
};

export default AddClientHealthChecksDialog;